import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

/**
 * One-line confirmation in the design's ink-on-paper inversion ("Bottle
 * logged for Auggie at 9:41 pm"). Sits above the home indicator, auto-hides,
 * and is announced to screen readers. A toast can carry one action ("Undo")
 * so a destructive tap never needs a confirm dialog.
 */
export interface ToastOptions {
  /** Decorative, before the message; hidden from screen readers. */
  emoji?: string;
  actionLabel?: string;
  onAction?: () => void;
  durationMs?: number;
  /** Runs when the toast expires or is dismissed/replaced, but not on Undo. */
  onDismiss?: () => void;
}

interface ToastValue {
  show: (message: string, options?: ToastOptions) => void;
  dismiss: () => void;
}

const ToastContext = createContext<ToastValue>({ show: () => {}, dismiss: () => {} });

const TOAST_MS = 2200;
const TOAST_WITH_ACTION_MS = 5000;

interface Current {
  message: string;
  emoji?: string;
  actionLabel?: string;
  onAction?: () => void;
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [current, setCurrent] = useState<Current | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const active = useRef<ToastOptions | null>(null);
  const dismiss = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    const previous = active.current;
    active.current = null;
    setCurrent(null);
    previous?.onDismiss?.();
  }, []);

  useEffect(() => {
    window.addEventListener("pagehide", dismiss);
    return () => {
      window.removeEventListener("pagehide", dismiss);
      dismiss();
    };
  }, [dismiss]);

  const show = useCallback(
    (message: string, options: ToastOptions = {}) => {
      dismiss();
      active.current = options;
      setCurrent({
        message,
        emoji: options.emoji,
        actionLabel: options.actionLabel,
        onAction: options.onAction,
      });
      const ms = options.durationMs ?? (options.onAction ? TOAST_WITH_ACTION_MS : TOAST_MS);
      timer.current = setTimeout(dismiss, ms);
    },
    [dismiss],
  );

  const value = useMemo(() => ({ show, dismiss }), [show, dismiss]);

  function pause() {
    if (timer.current) clearTimeout(timer.current);
  }

  function resume() {
    pause();
    const options = active.current;
    if (options)
      timer.current = setTimeout(
        dismiss,
        options.durationMs ?? (options.onAction ? TOAST_WITH_ACTION_MS : TOAST_MS),
      );
  }

  function act() {
    if (timer.current) clearTimeout(timer.current);
    const fn = active.current?.onAction;
    active.current = null;
    setCurrent(null);
    fn?.();
  }

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        aria-live="polite"
        className="pointer-events-none fixed inset-x-5 z-40 mx-auto max-w-[440px]"
        style={{ bottom: "calc(26px + env(safe-area-inset-bottom))" }}
      >
        {current ? (
          <div
            className="pointer-events-auto flex items-center gap-3 rounded-md bg-ink px-4 py-3 text-[14px] text-paper shadow-sheet"
            onPointerEnter={pause}
            onPointerLeave={(e) => {
              if (!e.currentTarget.contains(document.activeElement)) resume();
            }}
            onFocusCapture={pause}
            onBlurCapture={(e) => {
              if (!e.currentTarget.contains(e.relatedTarget)) resume();
            }}
          >
            {current.emoji ? (
              <span aria-hidden="true" className="shrink-0 text-[18px] leading-none">
                {current.emoji}
              </span>
            ) : null}
            <span className="min-w-0 flex-1">{current.message}</span>
            {current.onAction ? (
              <button
                type="button"
                onClick={act}
                className="heading -my-2 -mr-2 min-h-[44px] shrink-0 px-3 text-[15px] text-paper underline underline-offset-4"
              >
                {current.actionLabel ?? "Undo"}
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastValue {
  return useContext(ToastContext);
}
